/**
 * Status badges. Never colour alone: every badge has a shape glyph and a word, so the
 * document statuses read in greyscale and for colour-blind users.
 */
import type { ReactNode } from 'react';
import type { ApprovalState, DocumentStatus } from '../../lib/types';
import { TONE_SOFT, cx, type Tone } from './core';

type Glyph = 'dot' | 'ring' | 'square' | 'diamond' | 'bar' | 'cross' | 'half';

function GlyphMark({ g }: { g: Glyph }) {
  const c = 'h-2 w-2 shrink-0';
  switch (g) {
    case 'ring':
      return <span aria-hidden className={cx(c, 'rounded-full border-[1.5px] border-current')} />;
    case 'square':
      return <span aria-hidden className={cx(c, 'rounded-[1px] bg-current')} />;
    case 'diamond':
      return <span aria-hidden className={cx(c, 'rotate-45 scale-90 rounded-[1px] bg-current')} />;
    case 'bar':
      return <span aria-hidden className="h-0.5 w-2.5 shrink-0 bg-current" />;
    case 'cross':
      return <span aria-hidden className="shrink-0 text-[11px] font-bold leading-none">×</span>;
    case 'half':
      return <span aria-hidden className={cx(c, 'rounded-full border-[1.5px] border-current bg-[linear-gradient(90deg,currentColor_50%,transparent_50%)]')} />;
    default:
      return <span aria-hidden className={cx(c, 'rounded-full bg-current')} />;
  }
}

export function Badge({ tone = 'neutral', glyph = 'dot', children, className, title }: { tone?: Tone; glyph?: Glyph | null; children: ReactNode; className?: string; title?: string }) {
  return (
    <span title={title} className={cx('inline-flex h-6 items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 text-xs font-semibold', TONE_SOFT[tone], className)}>
      {glyph && <GlyphMark g={glyph} />}
      {children}
    </span>
  );
}

type Spec = { label: string; tone: Tone; glyph: Glyph; /** One line for the tooltip and screen readers. */ hint?: string };

/** Document statuses (PRD 7.3–7.9). Shape + word + colour: readable in greyscale. */
export const DOCUMENT_STATUS: Record<DocumentStatus, Spec> = {
  UPLOADING: { label: 'Uploading', tone: 'info', glyph: 'half', hint: 'The file is still being sent.' },
  QUARANTINED: { label: 'Quarantined', tone: 'danger', glyph: 'diamond', hint: 'Held by the virus check. Nobody can open it until it is cleared.' },
  PROCESSING: { label: 'Processing', tone: 'violet', glyph: 'half', hint: 'Being read (OCR) and indexed. It becomes searchable when done.' },
  NEEDS_REVIEW: { label: 'Needs review', tone: 'warn', glyph: 'bar', hint: 'Waiting in the inbox for someone to check its type and fields.' },
  READY: { label: 'Ready', tone: 'ok', glyph: 'dot', hint: 'Filed and searchable.' },
  FAILED: { label: 'Failed', tone: 'danger', glyph: 'cross', hint: 'Processing stopped. Retry it or upload the file again.' },
  TRASHED: { label: 'In trash', tone: 'neutral', glyph: 'ring', hint: 'Deleted. It can be restored until the trash is emptied.' },
  DISPOSED: { label: 'Disposed', tone: 'neutral', glyph: 'cross', hint: 'Destroyed under an approved disposal. Only the record that it existed is kept.' },
};

/** QXM controlled revisions (PRD §5, DC-12): Draft bar, In review half, Approved ring, Effective dot, Superseded ring, Obsolete ×, Withdrawn ring. */
export type RevisionStateName = 'DRAFT' | 'IN_REVIEW' | 'APPROVED' | 'EFFECTIVE' | 'SUPERSEDED' | 'OBSOLETE' | 'WITHDRAWN';
export const REVISION_STATE: Record<RevisionStateName, Spec> = {
  DRAFT: { label: 'Draft', tone: 'warn', glyph: 'bar', hint: 'Being written. Not for use.' },
  IN_REVIEW: { label: 'In review', tone: 'info', glyph: 'half', hint: 'Sent for review and approval (e-Sign). Not for use yet.' },
  APPROVED: { label: 'Approved', tone: 'violet', glyph: 'ring', hint: 'Approved; waiting for its effective date.' },
  EFFECTIVE: { label: 'Effective', tone: 'ok', glyph: 'dot', hint: 'The revision in force. Readers see this one.' },
  SUPERSEDED: { label: 'Superseded', tone: 'neutral', glyph: 'ring', hint: 'Replaced by a newer effective revision. Kept for the record.' },
  OBSOLETE: { label: 'Obsolete', tone: 'danger', glyph: 'cross', hint: 'Withdrawn from use with a reason. Kept for the record.' },
  WITHDRAWN: { label: 'Withdrawn', tone: 'neutral', glyph: 'ring', hint: 'A draft or approval that was withdrawn.' },
};

/** QXM change requests (Q2, CC-01): Open bar, Assessed half, Approved ring, Rejected ×, Implemented dot, Cancelled ring. */
export type CrStatusName = 'CR_OPEN' | 'CR_ASSESSED' | 'CR_APPROVED' | 'CR_REJECTED' | 'CR_IMPLEMENTED' | 'CR_CANCELLED';
export const CR_STATE: Record<CrStatusName, Spec> = {
  CR_OPEN: { label: 'Open', tone: 'warn', glyph: 'bar', hint: 'Raised; waiting for the Document Controller to assess it.' },
  CR_ASSESSED: { label: 'Assessed', tone: 'info', glyph: 'half', hint: 'Impact, assignee and due date set; waiting for approval.' },
  CR_APPROVED: { label: 'Approved', tone: 'violet', glyph: 'ring', hint: 'Approved; a revision can be started from it.' },
  CR_REJECTED: { label: 'Rejected', tone: 'danger', glyph: 'cross', hint: 'Rejected with a reason.' },
  CR_IMPLEMENTED: { label: 'Implemented', tone: 'ok', glyph: 'dot', hint: 'The revision made from it is in force.' },
  CR_CANCELLED: { label: 'Cancelled', tone: 'neutral', glyph: 'ring', hint: 'Cancelled with a reason.' },
};

/** Q3: controlled copies (OB-03), acknowledgements (RA-02) and external documents (ED-01). */
export type CopyStatusName = 'ISSUED' | 'RECALLED' | 'DESTROYED' | 'LOST';
export const COPY_STATE: Record<CopyStatusName, Spec> = {
  ISSUED: { label: 'Issued', tone: 'ok', glyph: 'dot', hint: 'In circulation with its holder.' },
  RECALLED: { label: 'Recalled', tone: 'neutral', glyph: 'ring', hint: 'Returned to the Document Controller.' },
  DESTROYED: { label: 'Destroyed', tone: 'neutral', glyph: 'cross', hint: 'Destroyed after recall; who and when are recorded.' },
  LOST: { label: 'Lost', tone: 'danger', glyph: 'diamond', hint: 'Could not be recovered; the note says what is known.' },
};
export type AckStatusName = 'ASSIGNED' | 'OVERDUE' | 'ACKED' | 'CANCELLED';
export const ACK_STATE: Record<AckStatusName, Spec> = {
  ASSIGNED: { label: 'To read', tone: 'warn', glyph: 'bar', hint: 'Assigned, not yet acknowledged.' },
  OVERDUE: { label: 'Overdue', tone: 'danger', glyph: 'diamond', hint: 'Past its due date.' },
  ACKED: { label: 'Acknowledged', tone: 'ok', glyph: 'dot', hint: 'Read and confirmed.' },
  CANCELLED: { label: 'Cancelled', tone: 'neutral', glyph: 'ring', hint: 'No longer required (revision replaced, or left the distribution).' },
};
export type ExternalStatusName = 'CURRENT' | 'SUPERSEDED' | 'WITHDRAWN';
export const EXTERNAL_STATE: Record<ExternalStatusName, Spec> = {
  CURRENT: { label: 'Current', tone: 'ok', glyph: 'dot', hint: 'The edition the organisation works to.' },
  SUPERSEDED: { label: 'Superseded', tone: 'neutral', glyph: 'ring', hint: 'A newer edition is on record.' },
  WITHDRAWN: { label: 'Withdrawn', tone: 'danger', glyph: 'cross', hint: 'No longer applies.' },
};
export const CopyChip = fromMap(COPY_STATE);
export const AckChip = fromMap(ACK_STATE);
export const ExternalChip = fromMap(EXTERNAL_STATE);

export function CrChip({ value, className }: { value: CrStatusName; className?: string }) {
  const s = CR_STATE[value] ?? { label: String(value), tone: 'neutral' as Tone, glyph: 'dot' as Glyph };
  return <Badge tone={s.tone} glyph={s.glyph} className={className} title={s.hint ? `${s.label}: ${s.hint}` : s.label}>{s.label}</Badge>;
}

export function RevisionChip({ value, label, className, compact }: { value: RevisionStateName; /** e.g. "Rev B" shown before the state word. */ label?: string; className?: string; compact?: boolean }) {
  const s = REVISION_STATE[value] ?? { label: String(value), tone: 'neutral' as Tone, glyph: 'dot' as Glyph };
  return (
    <Badge tone={s.tone} glyph={s.glyph} className={cx(compact && 'px-1.5', className)} title={s.hint ? `${s.label}: ${s.hint}` : s.label}>
      {label && <span className="code">{label}</span>}
      <span className={compact ? 'sr-only' : undefined}>{s.label}</span>
    </Badge>
  );
}

export const APPROVAL_STATE: Record<ApprovalState, Spec> = {
  PENDING: { label: 'Waiting for approval', tone: 'warn', glyph: 'half' },
  APPROVED: { label: 'Approved', tone: 'ok', glyph: 'dot' },
  REJECTED: { label: 'Rejected', tone: 'danger', glyph: 'cross' },
  CANCELLED: { label: 'Cancelled', tone: 'neutral', glyph: 'cross' },
};

function fromMap<K extends string>(map: Record<K, Spec>) {
  return function MappedBadge({ value, className }: { value: K; className?: string }) {
    const s = map[value] ?? { label: String(value), tone: 'neutral' as Tone, glyph: 'dot' as Glyph };
    return (
      <Badge tone={s.tone} glyph={s.glyph} className={className}>
        {s.label}
      </Badge>
    );
  };
}

export const ApprovalBadge = fromMap(APPROVAL_STATE);

/** Document status chip. `compact` hides the word on tight rows (the tooltip and sr text keep it). */
export function StatusChip({ value, className, compact }: { value: DocumentStatus; className?: string; compact?: boolean }) {
  const s = DOCUMENT_STATUS[value] ?? { label: String(value), tone: 'neutral' as Tone, glyph: 'dot' as Glyph };
  return (
    <Badge tone={s.tone} glyph={s.glyph} className={cx(compact && 'px-1.5', className)} title={s.hint ? `${s.label}: ${s.hint}` : s.label}>
      <span className={compact ? 'sr-only' : undefined}>{s.label}</span>
    </Badge>
  );
}

/**
 * A call number: the archive serial number (ASN) or a document reference, typed like a
 * catalogue card. Typewriter face, no box — the type itself is the signal.
 */
export function CallNumber({ value, label = 'ASN', className }: { value: string; label?: string; className?: string }) {
  return (
    <span className={cx('code whitespace-nowrap text-[13px] text-muted', className)} title={`${label} ${value}`}>
      <span className="sr-only">{label} </span>
      {value}
    </span>
  );
}
